import React, { useMemo } from 'react'
import MultiTreeSelect from '../../treeSelect/MultiTreeSelect'
import type { Id, Sentiment, CategoryChangeData } from '../../treeSelect/types'

interface CategoryTreeSelectWrapperProps {
	feedbackId: number
	questionId: number
	currentCategories: Record<number, { sentiment: number | null; title: string }>
	preloadedTreeData: Record<string, Array<Record<string, unknown>>> | null
	preloadedActive: Array<number> | null
	treeSelectEndpoint: string
	// Only show checkboxes on leaf nodes
	leafOnly?: boolean
	// Callback when categories change
	onChange?: (questionId: number, data: CategoryChangeData) => void
	translations: Record<string, string>
}

/**
 * Wrapper component for MultiTreeSelect in category editing
 *
 * This component is memoized to prevent unnecessary re-renders when the parent
 * CategoryEditDialog re-renders due to state changes (like tab switching or saving).
 *
 * By extracting the calculations into this component with useMemo, we ensure that:
 * 1. MultiTreeSelect doesn't remount when parent re-renders
 * 2. Search functionality works correctly without losing focus
 * 3. Dropdown stays open during user interaction
 * 4. Internal state (selections, sentiments) is preserved
 */
export const CategoryTreeSelectWrapper: React.FC<CategoryTreeSelectWrapperProps> = React.memo(
	({
		feedbackId,
		questionId,
		currentCategories,
		preloadedTreeData,
		preloadedActive,
		treeSelectEndpoint,
		leafOnly = false,
		onChange,
		translations,
	}) => {
		// Memoize calculations - only recalculate when currentCategories changes
		const categoryData = useMemo(() => {
			const categoryIds = Object.keys(currentCategories).map((id) => parseInt(id, 10))
			const categoryTitles: string[] = []
			const initialSentiments: Record<string, Sentiment> = {}

			Object.entries(currentCategories).forEach(([catId, catData]) => {
				// Handle new format {sentiment, title}
				if (typeof catData === 'object' && catData !== null && 'title' in catData) {
					initialSentiments[catId] = (catData.sentiment as Sentiment) ?? 0
					categoryTitles.push(catData.title)
				} else {
					// Fallback for old format (sentiment only)
					initialSentiments[catId] = (catData as Sentiment) ?? 0
				}
			})

			return { categoryIds, categoryTitles, initialSentiments }
		}, [currentCategories])

		// Memoize endpoint URL
		const endpoint = useMemo(() => `${treeSelectEndpoint}&questionId=${questionId}`, [treeSelectEndpoint, questionId])

		// Stable input ID
		const inputId = `categories_${questionId}`

		// Handle category changes from MultiTreeSelect
		const handleChange = useMemo(
			() =>
				onChange
					? (data: CategoryChangeData) => {
							onChange(questionId, data)
						}
					: undefined,
			[onChange, questionId],
		)

		return (
			<>
				<MultiTreeSelect
					endpoint={endpoint}
					titleKey="title"
					valueKey="text_analytics_category"
					multiple={true}
					selectParents={false}
					isSearchable={true}
					defaultValue={categoryData.categoryIds.length > 0 ? categoryData.categoryIds : null}
					defaultTitle={categoryData.categoryTitles.length > 0 ? categoryData.categoryTitles : null}
					placeholder={translations.selectCategories || 'Select categories...'}
					inputId={inputId}
					preloadedData={preloadedTreeData}
					preloadedActive={preloadedActive}
					autoOpen={false}
					leafOnly={leafOnly}
					withSentiment={true}
					questionId={questionId}
					initialSentiments={
						Object.keys(categoryData.initialSentiments).length > 0 ? categoryData.initialSentiments : null
					}
					onChange={handleChange}
					translations={translations}
				/>
				{/* Hidden input to store selected categories with sentiments */}
				<input type="hidden" id={inputId} name={inputId} defaultValue="" />
			</>
		)
	},
)

CategoryTreeSelectWrapper.displayName = 'CategoryTreeSelectWrapper'
